import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SalesStatementListResponse } from './sales-statement.model';
import { PAGE_SIZE_DEFAULT } from 'src/app/_common/constants';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})

export class SalesStatementService {
  constructor(private http: HttpClient,
    @Inject('API_URL') private apiUrl: string
  ) { }

  private printSalesStatementSubject = new Subject<any>();
  private downloadSalesStatementSubject = new Subject<any>();
  private downloadSalesStatementBasedOnItemsSubject = new Subject<any>();
  private canPrintSalesStatementSubject = new BehaviorSubject<boolean>(false);
  private canDownloadSalesStatementSubject = new BehaviorSubject<boolean>(false);
  private canDownloadSalesStatementBasedOnItemsSubject = new BehaviorSubject<boolean>(false);

  printSalesStatement$ = this.printSalesStatementSubject.asObservable();
  downloadSalesStatement$ = this.downloadSalesStatementSubject.asObservable();
  downloadSalesStatementBasedOnItems$ = this.downloadSalesStatementBasedOnItemsSubject.asObservable();

  print() {
    this.printSalesStatementSubject.next();
  }

  download(type: string) {
    this.downloadSalesStatementSubject.next(type);
  }

  downloadSalesStatementBasedOnItems() {
    this.downloadSalesStatementBasedOnItemsSubject.next();
  }

  get canPrint(): boolean {
    return this.canPrintSalesStatementSubject.value;
  }

  set canPrint(value: boolean) {
    this.canPrintSalesStatementSubject.next(value);
  }
  get canDownload(): boolean {
    return this.canDownloadSalesStatementSubject.value;
  }

  set canDownload(value: boolean) {
    this.canDownloadSalesStatementSubject.next(value);
  }
  
  get canDownloadBasedOnItems(): boolean {
    return this.canDownloadSalesStatementBasedOnItemsSubject.value;
  }

  set canDownloadBasedOnItems(value: boolean) {
    this.canDownloadSalesStatementBasedOnItemsSubject.next(value);
  }

  getSummary(filters: any): Observable<SalesStatementListResponse> {
    return this.http.post<SalesStatementListResponse>(
      this.apiUrl + 'salesStatement/summary', {
        filter: JSON.stringify(filters)
      }
    );
  }

  downloadCsv(filters: any) {
    return this.http.post(this.apiUrl + 'salesStatement/download-csv', 
      { filter: JSON.stringify(filters) },
      {
        observe: 'response',
        responseType: 'blob'
      }
    ).pipe(
      map((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = this.getFileName(contentDisposition, 'csv');
        return { body: response.body as Blob, filename, status: response.status };
      })
    );
  }

  downloadPdf(filters: any) {
    return this.http.post(this.apiUrl + 'salesStatement/download-pdf', 
      { filter: JSON.stringify(filters) },
      {
        observe: 'response',
        responseType: 'blob'
      }
    ).pipe(
      map((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = this.getFileName(contentDisposition, 'pdf');
        return { body: response.body as Blob, filename, status: response.status };
      })
    );
  }

  downloadXlsx(filters: any) {
    return this.http.post(this.apiUrl + 'salesStatement/download-xlsx', 
      { filter: JSON.stringify(filters) },
      {
        observe: 'response',
        responseType: 'blob'
      }
    ).pipe(
      map((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = this.getFileName(contentDisposition, 'xlsx');
        return { body: response.body as Blob, filename, status: response.status };
      })
    );
  }

  downloadSalesStatementBasedOnItemsCsv(filters: any) {
    return this.http.post(this.apiUrl + 'salesStatement/basedOnItems/download-csv', 
      { filter: JSON.stringify(filters) },
      {
        observe: 'response',
        responseType: 'blob'
      }
    ).pipe(
      map((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = this.getFileName(contentDisposition, 'csv');
        return { body: response.body as Blob, filename, status: response.status };
      })
    );
  }

  private getFileName(contentDisposition: string, type: string) {
    let filename = 'SalesStatement.' + type;
    if (contentDisposition) {
      const filenameStarMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);

      const filenameMatch = /filename\s*=\s*"?([^;"]+)"?/i.exec(contentDisposition);

      if (filenameStarMatch && filenameStarMatch[1]) {
        filename = decodeURIComponent(filenameStarMatch[1]);
      } else if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    return filename;
  }
}
